import React from 'react'
import { Icon } from '@iconify/react'

// Ant Design components
import { Avatar, Dropdown, Tooltip } from 'antd'

// Constants
import {
    CO7_ROLE_LABELS,
    CO7_MEMBER_ROLES,
    canEditCo7Collection,
    canManageCo7Collection,
    isCo7CollectionArchived,
} from '../co7.constants'

// Utils
import { normalizeCo7Member } from '../co7.slice'

// Styles
import styles from '../CO7Page.module.css'

const getInitials = (name) => {
    if (!name) return 'C'
    const parts = String(name).trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return String(name).slice(0, 2).toUpperCase()
}

const CollectionCard = ({
    collection,
    isSuperAdmin,
    onEdit,
    onArchive,
    onManageMembers,
}) => {
    const isOwner = collection?.effectiveRole === CO7_MEMBER_ROLES.OWNER
    const isArchived = isCo7CollectionArchived(collection)
    const canManage = canManageCo7Collection(
        collection?.effectiveRole,
        isSuperAdmin
    )
    const canEdit = canEditCo7Collection(collection, isSuperAdmin)
    const members = (collection?.members || []).map(normalizeCo7Member)
    const memberCount = collection?.memberCount ?? members.length ?? 0
    const previewMembers = members.slice(0, 4)
    const showMemberPreview = previewMembers.length > 0

    const menuItems = [
        {
            key: 'members',
            label: 'Manage members',
            icon: <Icon icon="mdi:account-group-outline" width={16} />,
            onClick: () => onManageMembers?.(collection),
        },
        ...(canEdit
            ? [
                  {
                      key: 'edit',
                      label: 'Edit collection',
                      icon: <Icon icon="mdi:pencil-outline" width={16} />,
                      onClick: () => onEdit?.(collection),
                  },
              ]
            : []),
        ...(canManage
            ? [
                  ...(canEdit ? [{ type: 'divider' }] : []),
                  {
                      key: 'archive',
                      label: isArchived ? 'Unarchive' : 'Archive',
                      danger: !isArchived,
                      icon: (
                          <Icon
                              icon={
                                  isArchived
                                      ? 'mdi:archive-arrow-up-outline'
                                      : 'mdi:archive-outline'
                              }
                              width={16}
                          />
                      ),
                      onClick: () => onArchive?.(collection),
                  },
              ]
            : []),
    ]

    return (
        <article
            className={`${styles.collectionCard} ${isArchived ? styles.collectionCardArchived : ''}`}
        >
            {isArchived ? (
                <span className={styles.archivedBadge}>Archived</span>
            ) : null}
            <div className={styles.collectionCardTop}>
                <div
                    className={`${styles.collectionIconWrap} ${isArchived ? styles.collectionIconWrapArchived : ''}`}
                >
                    <Icon
                        icon="mdi:folder-multiple-outline"
                        width={22}
                        height={22}
                    />
                </div>
                <div className={styles.collectionCardActions}>
                    {isOwner ? (
                        <span className={styles.ownerBadge}>Owner</span>
                    ) : null}
                    <Dropdown
                        menu={{ items: menuItems }}
                        trigger={['click']}
                        placement="bottomRight"
                    >
                        <button
                            type="button"
                            className={styles.cardMenuBtn}
                            aria-label="Collection actions"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Icon icon="mdi:dots-horizontal" width={20} />
                        </button>
                    </Dropdown>
                </div>
            </div>

            <div className={styles.collectionCardBody}>
                <h3 className={styles.collectionName}>{collection?.name}</h3>
                <p className={styles.collectionDescription}>
                    {collection?.description || 'No description yet'}
                </p>
            </div>

            <div className={styles.collectionCardFooter}>
                {showMemberPreview ? (
                    <div className={styles.memberAvatars}>
                        {previewMembers.map((member) => (
                            <Tooltip
                                key={member.userId}
                                title={`${member.name} · ${CO7_ROLE_LABELS[member.role] || member.role}`}
                            >
                                <Avatar
                                    size={36}
                                    src={member.profilePicture}
                                    className={styles.memberAvatar}
                                    style={{
                                        flexShrink: 0,
                                        backgroundColor:
                                            member.role ===
                                            CO7_MEMBER_ROLES.OWNER
                                                ? '#6366f1'
                                                : '#94a3b8',
                                    }}
                                >
                                    {getInitials(member.name)}
                                </Avatar>
                            </Tooltip>
                        ))}
                        {memberCount > 4 ? (
                            <span className={styles.memberOverflow}>
                                +{memberCount - 4}
                            </span>
                        ) : null}
                    </div>
                ) : (
                    <span className={styles.membersLink}>
                        <Icon icon="mdi:account-group-outline" width={16} />
                        Manage members
                    </span>
                )}
                {showMemberPreview ? (
                    <span className={styles.memberCount}>
                        {memberCount} member{memberCount === 1 ? '' : 's'}
                    </span>
                ) : null}
            </div>
        </article>
    )
}

export default CollectionCard
